import React from "react";
import AsyncSelect from "react-select/async";
import { useLoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "../../config/googleMaps";

const libraries = ["places"];

const PlaceSearchInput = ({
  value,
  onChange,
  placeholder = "Search for a place...",
  hasError = false,
  className = "",
  types = [], // Optional array of place types to restrict search
  componentRestrictions = null, // Add support for country restrictions
    isDisabled = false,
  fields = [],
  instanceId = "default",
  placeId = null,
}) => {
  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Styles for the select component
  const getSelectStyles = (hasError) => ({
    control: (base) => ({
      ...base,
      minHeight: '32px',
      height: '32px',
      backgroundColor: 'white',
      borderColor: hasError ? '#ef4444' : '#d1d5db',
      borderRadius: '0.5rem',
      boxShadow: 'none',
      '&:hover': {
        borderColor: hasError ? '#ef4444' : '#d1d5db'
      },
      padding: '0',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
      height: '32px',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      fontSize: '0.875rem',
      fontWeight: '500',
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '0.875rem',
      color: '#9ca3af',
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: '0.875rem',
      fontWeight: '500',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '4px 8px',
      height: '32px',
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '4px 8px',
      height: '32px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: (base) => ({
      ...base,
      fontSize: '0.875rem',
      marginTop: '4px',
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '0.875rem',
      backgroundColor: state.isFocused ? '#f3f4f6' : 'white',
      color: state.isFocused ? '#111827' : '#374151',
      '&:active': {
        backgroundColor: '#e5e7eb',
      },
    }),
  });

  // Load place options
  const loadPlaceOptions = async (inputValue) => {
    const query = String(inputValue || "").trim();
    if (query.length < 2 || !window.google?.maps?.places) {
      return [];
    }
    try {
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      const predictions = await new Promise((resolve, reject) => {
        autocompleteService.getPlacePredictions(
          {
            input: query,
            ...(types.length > 0 && { types }),
            ...(componentRestrictions && { componentRestrictions }),
          },
          (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(results);
            } else {
              resolve([]);
            }
          }
        );
      });

      return predictions.map((prediction) => ({
        value: prediction.place_id,
        label: prediction.description,
      }));
    } catch (error) {
      console.error("Error searching places:", error);
      return [];
    }
  };

  // Get place details
  const getPlaceDetails = (placeId) => {
    return new Promise((resolve, reject) => {
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails(
        { 
          placeId, 
          fields: ["geometry", "name", "place_id", "address_components"] 
        }, 
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
            resolve({
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              name: place.name,
              place_id: place.place_id,
              address_components: place.address_components
            });
          } else {
            resolve({ lat: null, lng: null, name: null, formatted_address: null, place_id: null });
          }
        }
      );
    });
  };

  // Handle place selection
  const handlePlaceSelect = async (selectedOption) => {
    if (selectedOption) {
      if (String(selectedOption.value).startsWith("saved-")) {
        return;
      }
      const placeDetails = await getPlaceDetails(selectedOption.value);
      onChange({
        ...placeDetails,
        name: placeDetails.name || selectedOption.label || "",
      });
    } else {
      onChange(null);
    }
  };

  const selectValue = value
    ? { label: value, value: placeId || `saved-${instanceId}` }
    : null;

  const noOptionsMessage = ({ inputValue }) => {
    const query = String(inputValue || "").trim();
    if (query.length < 2) {
      return "Type at least 2 characters to search";
    }
    return "No places found";
  };

  if (loadError) {
    return <div className="text-red-500 text-sm">Error loading Google Maps</div>;
  }

  if (!isLoaded) {
    return <div className="text-gray-500 text-sm">Loading...</div>;
  }

  return (
    <AsyncSelect
      key={instanceId}
      inputId={`place-search-${instanceId}`}
      cacheOptions
      loadOptions={loadPlaceOptions}
      onChange={handlePlaceSelect}
      value={selectValue}
      placeholder={placeholder}
      isClearable
      isSearchable
      isDisabled={isDisabled}
      openMenuOnClick={false}
      openMenuOnFocus={false}
      noOptionsMessage={noOptionsMessage}
      className={`text-sm ${className}`}
      classNamePrefix="react-select"
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      styles={getSelectStyles(hasError)}
    />
  );
};

export default PlaceSearchInput; 